import { type NoReactionExplanation } from "@/data/reactions";
import { motion } from "framer-motion";
import { Info, Flame, Snowflake } from "@/lib/icons";
import { forwardRef } from "react";

interface NoReactionExplainerProps {
  explanation: NoReactionExplanation;
}

/**
 * Friendly card shown when chemicals are mixed but no reaction triggers.
 * Pairs an icon with text so meaning isn't conveyed by color alone.
 */
const NoReactionExplainer = forwardRef<HTMLDivElement, NoReactionExplainerProps>(
  function NoReactionExplainer({ explanation }, ref) {
    const isHeat = explanation.reason === "needs-heat";
    return (
      <motion.div
        ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-2xl mx-auto"
      role="status"
    >
      <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-muted/30">
        <div
          className={`mt-0.5 flex-shrink-0 p-1.5 rounded-md ${
            isHeat
              ? "bg-orange-500/10 text-orange-500"
              : "bg-muted text-muted-foreground"
          }`}
          aria-hidden="true"
        >
          {isHeat ? <Flame size={14} /> : explanation.reason === "spectator-ions" ? <Snowflake size={14} /> : <Info size={14} />}
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground mb-0.5">
            {isHeat
              ? "Needs more heat"
              : explanation.reason === "spectator-ions"
                ? "No net reaction - spectator ions"
                : explanation.reason === "single-chemical"
                  ? "Add another chemical"
                  : explanation.reason === "same-state-gases"
                    ? "Needs ignition or heat"
                    : "No reaction observed"}
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">{explanation.message}</p>
        </div>
      </div>
      </motion.div>
    );
  },
);

export default NoReactionExplainer;